import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import { Header, Footer } from "../App";
import { MatchCard, AccessoryCard } from "../components/cards";
import { QUESTIONS, DECKS as FALLBACK_DECKS, EXTRAS as FALLBACK_EXTRAS } from "../data/quizData";
import {
  SECTION_TITLES, ROUND_INTRO, ROUND_DIMENSIONS, LOADING_QUOTES, RANK_LABELS,
  sectionKeyOf, scoreDecks, pickMatches, pickExtras,
} from "../quizLogic";

function roundOf(i) {
  const q = QUESTIONS[i];
  return q ? q.section : null;
}
function indexInSection(i) {
  const section = sectionKeyOf(QUESTIONS[i]);
  return i - QUESTIONS.findIndex((q) => sectionKeyOf(q) === section);
}
function countInSection(section) {
  return QUESTIONS.filter((q) => sectionKeyOf(q) === section).length;
}

function SwipeCard({ q, behind, onExpose, onSwiped }) {
  const ref = useRef(null);
  const dragRef = useRef({ startX: 0, dx: 0, dragging: false });
  const doneRef = useRef(false);

  useEffect(() => {
    if (behind || !ref.current) return;
    ref.current.focus({ preventScroll: true });
  }, [behind]);

  const finish = useCallback((liked, dx = 200) => {
    if (doneRef.current || !ref.current) return;
    doneRef.current = true;
    const el = ref.current;
    const flyX = liked ? 700 : -700;
    el.style.transition = "transform .3s ease, opacity .3s ease";
    el.style.transform = `translateX(${flyX}px) rotate(${dx / 10}deg)`;
    el.style.opacity = "0";
    setTimeout(() => onSwiped(q.id, liked), 220);
  }, [q, onSwiped]);

  useEffect(() => {
    if (behind) return;
    onExpose(finish);
    return () => onExpose(null);
  }, [behind, finish, onExpose]);

  const onPointerDown = (e) => {
    dragRef.current = { startX: e.clientX, dx: 0, dragging: true };
    ref.current.setPointerCapture(e.pointerId);
    ref.current.style.transition = "none";
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    d.dx = e.clientX - d.startX;
    const el = ref.current;
    el.style.transform = `translateX(${d.dx}px) rotate(${d.dx / 18}deg)`;
    el.querySelector(".stampmark.pass").style.opacity = Math.max(0, Math.min(1, -d.dx / 60));
    el.querySelector(".stampmark.match").style.opacity = Math.max(0, Math.min(1, d.dx / 60));
  };
  const onPointerUp = () => {
    const d = dragRef.current;
    if (!d.dragging) return;
    d.dragging = false;
    const el = ref.current;
    el.style.transition = "transform .25s ease";
    if (d.dx > 100) return finish(true, d.dx);
    if (d.dx < -100) return finish(false, d.dx);
    el.style.transform = "translateX(0) rotate(0)";
    el.querySelector(".stampmark.pass").style.opacity = 0;
    el.querySelector(".stampmark.match").style.opacity = 0;
    d.dx = 0;
  };

  return (
    <div ref={ref} className={`swipe-card ${behind ? "behind" : "front"}`}
      tabIndex={behind ? -1 : 0} role="group" aria-label={q.text}
      data-testid={behind ? undefined : "swipe-card"}
      onPointerDown={behind ? undefined : onPointerDown}
      onPointerMove={behind ? undefined : onPointerMove}
      onPointerUp={behind ? undefined : onPointerUp}
      onPointerCancel={behind ? undefined : onPointerUp}
      onKeyDown={behind ? undefined : (e) => {
        if (e.key === "ArrowRight") finish(true, 200);
        if (e.key === "ArrowLeft") finish(false, -200);
      }}>
      <div className="tab">{SECTION_TITLES[sectionKeyOf(q)].split("·")[1].trim()}</div>
      <div className="qtext">{q.text}</div>
      <div className="stampmark pass">PASS</div>
      <div className="stampmark match">MATCH</div>
    </div>
  );
}

export default function Quiz() {
  const { user } = useAuth();
  const [screen, setScreen] = useState("intro"); // intro | howto | quiz | loading | results | favorites
  const [searchParams, setSearchParams] = useSearchParams();
  const [decks, setDecks] = useState(FALLBACK_DECKS);
  const [extrasList, setExtrasList] = useState(FALLBACK_EXTRAS);
  const [favorites, setFavorites] = useState(new Set());
  const [owned, setOwned] = useState(new Set());
  const [scoreMap, setScoreMap] = useState({});
  const [idx, setIdx] = useState(0);
  const [swipes, setSwipes] = useState({});
  const [shown, setShown] = useState(new Set());
  const [ranked, setRanked] = useState(null);
  const [pendingMatches, setPendingMatches] = useState(null);
  const [lastRoundShown, setLastRoundShown] = useState(null);
  const [matches, setMatches] = useState([]);
  const [ribbonOn, setRibbonOn] = useState(true);
  const [accessories, setAccessories] = useState([]);
  const [quoteCount, setQuoteCount] = useState(0);
  const [showReveal, setShowReveal] = useState(false);
  const [resultId, setResultId] = useState(null);
  const [favReturnTo, setFavReturnTo] = useState("intro");
  const completedRef = useRef(false);
  const finishRef = useRef(null);

  useEffect(() => {
    api("/decks").then((d) => {
      if (d.decks?.length) setDecks(d.decks);
      if (d.extras?.length) setExtrasList(d.extras);
    }).catch(() => {});
    api("/favorites").then((f) => setFavorites(new Set(f.favorites))).catch(() => {});
    api("/owned").then((o) => setOwned(new Set(o.owned))).catch(() => {});
    // Scores from past attempts, so favorites render with the same bars + % badge.
    api("/quiz/history").then((res) => {
      const map = {};
      (res.results || []).forEach((r) => {
        [...(r.top3 || []), ...(r.more_matches || [])].forEach((m) => {
          if (m.deck && !map[m.deck.name]) map[m.deck.name] = m.scores;
        });
      });
      setScoreMap(map);
    }).catch(() => {});
  }, []);

  // Loading sequence: compute results immediately, then play the quotes, then reveal.
  useEffect(() => {
    if (screen !== "loading") return;
    window.scrollTo({ top: 0 });
    const r = scoreDecks(decks, swipes, QUESTIONS);
    const pm = pickMatches(r, new Set([...shown, ...owned]), 20, 3.0);
    setRanked(r);
    setPendingMatches(pm);
    setQuoteCount(0);
    setShowReveal(false);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const per = reduce ? 1400 : 2600;
    const timers = [];
    let i = 0;
    const tick = () => {
      i += 1;
      setQuoteCount(i);
      if (i < LOADING_QUOTES.length) timers.push(setTimeout(tick, per));
      else timers.push(setTimeout(() => setShowReveal(true), per));
    };
    timers.push(setTimeout(tick, 60));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  const startQuiz = (skipHowto = false) => {
    setIdx(0); setSwipes({}); setLastRoundShown(null);
    completedRef.current = false; setResultId(null);
    // "How To Take The Quiz" shows before Round I on every start, including retakes.
    setScreen(skipHowto ? "quiz" : "howto");
  };

  // Nav links land on /?favs=1, /?owned=1, /?start=1 - open the right screen on arrival.
  useEffect(() => {
    if (searchParams.get("favs")) {
      setFavReturnTo("intro");
      setScreen("favorites");
      refreshScores();
      setSearchParams({}, { replace: true });
      return;
    }
    if (searchParams.get("owned")) {
      setScreen("owned");
      window.scrollTo({ top: 0 });
      setSearchParams({}, { replace: true });
      return;
    }
    if (searchParams.get("start")) {
      startQuiz(); // Retake Quiz nav goes through the How-To page, same as a fresh start
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const currentRound = idx < QUESTIONS.length ? roundOf(idx) : null;
  const dividerShowing = screen === "quiz" && currentRound !== null && currentRound !== lastRoundShown;

  useEffect(() => {
    if (screen === "quiz" && idx >= QUESTIONS.length) setScreen("loading");
  }, [idx, screen]);

  useEffect(() => {
    if (screen !== "quiz" || dividerShowing) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") finishRef.current?.(true, 200);
      if (e.key === "ArrowLeft") finishRef.current?.(false, -200);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [screen, dividerShowing]);

  const onSwipe = useCallback((qid, liked) => {
    setSwipes((prev) => ({ ...prev, [qid]: liked }));
    setIdx((i) => i + 1);
  }, []);

  const exposeFinish = useCallback((fn) => { finishRef.current = fn; }, []);

  const toggleFav = useCallback((name) => {
    api("/favorites", { method: "POST", body: JSON.stringify({ deck_name: name }) })
      .then((res) => setFavorites(new Set(res.favorites)))
      .catch(() => {});
  }, []);

  const toggleOwned = useCallback((name) => {
    api("/owned", { method: "POST", body: JSON.stringify({ deck_name: name }) })
      .then((res) => setOwned(new Set(res.owned)))
      .catch(() => {});
  }, []);

  // Owned decks are never matched again - combined with the within-session
  // "shown" set before every pick, across "show me 3 more" and all retakes.
  const matchExcludeSet = useCallback(() => new Set([...shown, ...owned]), [shown, owned]);

  const toMatchEntry = (r, i) => ({
    rank: i + 1,
    rank_label: RANK_LABELS[i] || `MATCH No. ${i + 1}`,
    deck: r.deck,
    scores: { energy: r.energy, need: r.need, flavor: r.flavor, aesthetic: r.aesthetic, total: r.total },
  });

  const showResults = () => {
    const top3 = pendingMatches || pickMatches(ranked, matchExcludeSet(), 20, 3.0);
    setPendingMatches(null);
    const newShown = new Set(shown);
    top3.forEach((r) => newShown.add(r.deck.name));
    setShown(newShown);
    setMatches(top3);
    setRibbonOn(true);
    const ex = pickExtras(top3.map((r) => r.deck), extrasList);
    setAccessories(ex);
    setScreen("results");
    window.scrollTo({ top: 0 });
    if (!completedRef.current) {
      completedRef.current = true;
      api("/quiz/complete", {
        method: "POST",
        body: JSON.stringify({ swipes, top3: top3.map(toMatchEntry), extras: ex }),
      }).then((res) => setResultId(res.id)).catch(() => {});
    }
  };

  const showMore = () => {
    const more = pickMatches(ranked, matchExcludeSet(), 30, 3.0);
    const newShown = new Set(shown);
    more.forEach((r) => newShown.add(r.deck.name));
    setShown(newShown);
    setMatches(more);
    setRibbonOn(false);
    if (more.length) setAccessories(pickExtras(more.map((r) => r.deck), extrasList));
    if (resultId && more.length) {
      api(`/quiz/results/${resultId}/more`, {
        method: "PUT", body: JSON.stringify({ matches: more.map(toMatchEntry) }),
      }).catch(() => {});
    }
    window.scrollTo({ top: 0, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };

  const retake = () => {
    setIdx(0); setSwipes({}); setShown(new Set()); setRanked(null);
    setLastRoundShown(null); setPendingMatches(null); setMatches([]); setAccessories([]);
    completedRef.current = false; setResultId(null);
    setScreen("intro");
    window.scrollTo({ top: 0 });
  };

  const refreshScores = useCallback(() => {
    api("/quiz/history").then((res) => {
      const map = {};
      (res.results || []).forEach((r) => {
        [...(r.top3 || []), ...(r.more_matches || [])].forEach((m) => {
          if (m.deck && !map[m.deck.name]) map[m.deck.name] = m.scores;
        });
      });
      setScoreMap(map);
    }).catch(() => {});
  }, []);

  const openFavorites = (from) => {
    setFavReturnTo(from);
    setScreen("favorites");
    window.scrollTo({ top: 0 });
    refreshScores();
  };

  const trackEvent = useCallback((payload) => {
    api("/events", { method: "POST", body: JSON.stringify(payload) }).catch(() => {});
  }, []);

  const introSentence1 = "Hi, I'm Jen, your oracle BFF matchmaker!";
  const introMiddle =
    "I personally designed this app to match you with the perfect deck. " +
    "Tell me what you're drawn to, how you like to work your cards, what you want from a deck, and how you want it to look. " +
    `I'll use my 12+ years of reading and teaching oracle cards to work my magic in the background with ${decks.length} hand-picked oracle decks and find frequency matches for your energy & vibe.`;
  const introLast = "Ready to meet your new Oracle BFF?";

  const favDecks = decks.filter((d) => favorites.has(d.name));
  const favExtras = extrasList.filter((a) => favorites.has(a.name));
  const ownedDecks = decks.filter((d) => owned.has(d.name));

  return (
    <div className={`frame ${screen === "intro" ? "wide" : ""}`}>
      <Header favCount={favorites.size} onFavClick={() => openFavorites(screen)} />

      {screen === "intro" && (
        <section className="intro" id="screen-intro" data-testid="intro-screen">
          <div className="eyebrow">Meet the deck that gets you</div>
          <h1>There are thousands of oracle decks out there. Which one is your new BFF?</h1>

          <div className="intro-photo-row-desktop">
            <img className="intro-photo" src="/img/intro-desktop.png" alt="Jen, your oracle BFF matchmaker" />
            <p className="lede">
              <span className="lede-first">{introSentence1}</span><br />
              {introMiddle}<br />
              <span className="lede-last">{introLast}</span>
            </p>
          </div>

          <div className="intro-photo-row-mobile">
            <p className="lede"><span className="lede-first">{introSentence1}</span></p>
            <img className="intro-photo-mobile" src="/img/intro-mobile.png" alt="Jen, your oracle BFF matchmaker" />
            <p className="lede-rest">{introMiddle}<br /><span className="lede-last">{introLast}</span></p>
          </div>

          <div className="intro-facts" data-testid="intro-stats">
            <div><b data-testid="deck-count">{decks.length}</b>decks</div>
            <div><b>4</b>minutes</div>
            <div><b>3</b>matches</div>
          </div>
          <button className="stamp-btn solid" data-testid="start-quiz-button" onClick={() => startQuiz()}>
            Find My Oracle BFF
          </button>
          <div className="free-note">Completely free, including your results. No credit card needed.</div>
        </section>
      )}

      {screen === "howto" && (
        <section id="screen-howto" data-testid="howto-screen">
          <div className="howto-block">
            <h2 className="rd-title">How To Take The Quiz</h2>
            <p className="rd-sub">Swipe or click &quot;Not it&quot; or &quot;That's Me&quot; on the following questions to be matched with the perfect decks.</p>
            <p className="rd-sub">Need a specific kind of deck? Be ruthlessly picky about your answers. Don't say yes to everything.</p>
            <p className="rd-sub">Want lots of possibilities to choose from? Say yes to as much as you want and let your divine matches rise to the top.</p>
            <button className="stamp-btn solid" data-testid="howto-next-button" type="button"
              onClick={() => setScreen("quiz")}>Next →</button>
          </div>
        </section>
      )}

      {screen === "quiz" && (
        <section id="screen-quiz" data-testid="quiz-screen">
          <div className="quiz-head">
            <div className="round-label" data-testid="round-label">
              {!dividerShowing && currentRound && (
                <>
                  <b>{SECTION_TITLES[currentRound]}</b>
                  &nbsp;·&nbsp; card {indexInSection(idx) + 1} of {countInSection(currentRound)}
                </>
              )}
            </div>
            <div className="ticks" data-testid="round-ticks">
              {!dividerShowing && currentRound && (() => {
                const sectionQs = QUESTIONS.filter((q) => sectionKeyOf(q) === currentRound);
                const startIdx = QUESTIONS.findIndex((q) => sectionKeyOf(q) === currentRound);
                return sectionQs.map((q, i) => {
                  const g = startIdx + i;
                  return <div key={q.id} className={`tick ${g < idx ? "done" : g === idx ? "current" : ""}`} />;
                });
              })()}
            </div>
          </div>
          <div className="card-stage" data-testid="card-stage">
            {dividerShowing ? (
              <div className="round-divider" data-testid="round-divider">
                <div className="rd-num">Round {ROUND_INTRO[currentRound].num}</div>
                <h2 className="rd-title">{ROUND_INTRO[currentRound].title}</h2>
                <p className="rd-sub">{ROUND_INTRO[currentRound].sub}</p>
                <button className="stamp-btn solid" data-testid="round-continue-button" type="button"
                  onClick={() => setLastRoundShown(currentRound)}>Let's go →</button>
              </div>
            ) : (
              <>
                {QUESTIONS[idx + 1] && <SwipeCard key={QUESTIONS[idx + 1].id} q={QUESTIONS[idx + 1]} behind onExpose={exposeFinish} onSwiped={onSwipe} />}
                {QUESTIONS[idx] && <SwipeCard key={QUESTIONS[idx].id} q={QUESTIONS[idx]} onExpose={exposeFinish} onSwiped={onSwipe} />}
              </>
            )}
          </div>
          {!dividerShowing && (
            <>
              <div className="actions">
                <button className="action-btn pass" data-testid="pass-button"
                  onClick={() => finishRef.current?.(false, -200)}>✕ Not it</button>
                <button className="action-btn match" data-testid="match-button"
                  onClick={() => finishRef.current?.(true, 200)}>♥ That's me</button>
              </div>
              <p className="hint">Swipe the card, tap a button, or use ← / →</p>
            </>
          )}
        </section>
      )}

      {screen === "loading" && (
        <section id="screen-loading" data-testid="loading-screen">
          <div className="loading-quotes" hidden={showReveal}>
            {LOADING_QUOTES.slice(0, quoteCount).map((qt, i) => (
              <div key={i} className="loading-quote-line quote-in">{qt}</div>
            ))}
          </div>
          {!showReveal && (
            <div className="loading-dots"><span></span><span></span><span></span></div>
          )}
          <div className="loading-stage">
            <div className={`reveal-card ${showReveal ? "show" : ""}`} data-testid="reveal-card">
              <div className="rd-num">Your Results Are In</div>
              <p className="rd-quote">Ok, I've got someone you NEED to meet!</p>
              <button className="stamp-btn solid" data-testid="reveal-button" type="button" onClick={showResults}>
                Meet Your New Oracle BFF
              </button>
            </div>
          </div>
        </section>
      )}

      {screen === "results" && (
        <section id="screen-results" data-testid="results-screen">
          <div className="results-head">
            <div className="eyebrow">The Matchmaking Report</div>
            <h2>Your New BFF Circle</h2>
            <p>Save your faves for future reference. Mark decks you already own. Click on the &quot;Meet Your BFF&quot; buttons to view your matches. Go make friends and pull some cards!</p>
          </div>
          <div id="matchList" data-testid="match-list">
            {matches.length === 0 ? (
              <p style={{ textAlign: "center", color: "var(--ink-soft)", fontFamily: "var(--mono)", fontSize: "13px" }}>
                You've now met every deck in the drawer. Go ahead and{" "}
                <button className="retake" style={{ display: "inline" }} onClick={retake}>start over</button>.
              </p>
            ) : (
              matches.map((r, i) => (
                <MatchCard key={r.deck.name} d={r.deck} scored={r} rankNum={i + 1} showRibbon={ribbonOn}
                  isFav={favorites.has(r.deck.name)} onToggleFav={toggleFav}
                  isOwned={owned.has(r.deck.name)} onToggleOwned={toggleOwned}
                  onCtaClick={(deck, rank) => {
                    if (resultId) trackEvent({ type: "cta_click", result_id: resultId, deck_name: deck.name, rank });
                  }} />
              ))
            )}
          </div>
          {matches.length > 0 && (
            <div className="results-actions">
              <button className="stamp-btn" data-testid="more-button" onClick={showMore}>Show me 3 more</button>
              <p className="bff-circle-note">Most deck people end up with a whole circle of Oracle BFFs, not just one. No rule says you have to stop here.</p>
            </div>
          )}
          <div className="activation-block">
            <h3>Activate Your New Oracle BFF</h3>
            <p>Meeting your match is just the start. The real magic happens once you two actually bond. My <i>Intuitive Oracle Activation 5-Day Challenge</i> shows you exactly how to make your cards come alive with intuitive hits so you feel connected and guided through every card pull, and every reading becomes an accurate, intuitive message you trust.</p>
            <a className="stamp-btn gold" data-testid="challenge-link" href="https://iloveoraclecards.com/" target="_blank" rel="noopener"
              onClick={() => { if (resultId) trackEvent({ type: "challenge_click", result_id: resultId }); }}>Take the 5-Day Challenge</a>
          </div>
          {accessories.length > 0 && (
            <>
              <div className="accessory-head" data-testid="accessory-head">Pairs well with your matches</div>
              <div id="accessoryList">
                {accessories.map((a) => (
                  <AccessoryCard key={a.name} a={a} isFav={favorites.has(a.name)} onToggleFav={toggleFav} />
                ))}
              </div>
            </>
          )}
          <div className="results-actions">
            <button className="retake" data-testid="retake-button" onClick={retake}>Take the quiz again</button>
          </div>
          <p className="amazon-note">Some links above are affiliate links. Jen may earn from qualifying purchases (at no extra cost to you).</p>
        </section>
      )}

      {screen === "favorites" && (
        <section id="screen-favorites" data-testid="favorites-screen">
          <div className="results-head">
            <div className="eyebrow">saved by you</div>
            <h2>Your favorites</h2>
            <p>Every deck you've hearted, kept in one place.</p>
          </div>
          <div id="favList" data-testid="favorites-list">
            {favDecks.length === 0 && favExtras.length === 0 ? (
              <p style={{ textAlign: "center", color: "var(--ink-soft)", fontFamily: "var(--mono)", fontSize: "13px" }} data-testid="favorites-empty">
                No favorites yet. Heart a match after you take the quiz.
              </p>
            ) : (
              <>
                {favDecks.map((d) => (
                  <MatchCard key={d.name} d={d} scored={scoreMap[d.name] || null} rankNum={null} collapsible
                    isFav onToggleFav={toggleFav}
                    isOwned={owned.has(d.name)} onToggleOwned={toggleOwned} />
                ))}
                {favExtras.map((a) => (
                  <AccessoryCard key={a.name} a={a} isFav onToggleFav={toggleFav} />
                ))}
              </>
            )}
          </div>
          <div className="results-actions">
            <button className="retake" data-testid="back-from-favorites-button"
              onClick={() => setScreen(favReturnTo === "favorites" ? "intro" : favReturnTo)}>← Back</button>
          </div>
        </section>
      )}

      {screen === "owned" && (
        <section id="screen-owned" data-testid="owned-screen">
          <div className="results-head">
            <div className="eyebrow">your deck shelf</div>
            <h2>My Decks</h2>
            <p>Decks you've marked as already owned. Un-mark one any time to make it eligible for matches again.</p>
          </div>
          <div data-testid="owned-list">
            {ownedDecks.length === 0 ? (
              <p style={{ textAlign: "center", color: "var(--ink-soft)", fontFamily: "var(--mono)", fontSize: "13px" }} data-testid="owned-empty">
                No owned decks yet. Mark a deck as owned from your results.
              </p>
            ) : (
              ownedDecks.map((d) => (
                <MatchCard key={d.name} d={d} scored={scoreMap[d.name] || null} rankNum={null} collapsible
                  isFav={favorites.has(d.name)} onToggleFav={toggleFav}
                  isOwned onToggleOwned={toggleOwned} />
              ))
            )}
          </div>
          <div className="results-actions">
            <button className="retake" data-testid="back-from-owned-button"
              onClick={() => setScreen("intro")}>← Back</button>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
